import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowDown } from 'lucide-react';
interface MetricStairsCompactProps {
  type: 'length' | 'volume' | 'weight';
  from?: string;
  to?: string;
  value?: number;
  highlight?: string;
  showAnimation?: boolean;
}
const METRIC_UNITS = {
  length: ['km', 'hm', 'dam', 'm', 'dm', 'cm', 'mm'],
  volume: ['kl', 'hl', 'dal', 'l', 'dl', 'cl', 'ml', 'm3'],
  weight: ['kg', 'hg', 'dag', 'g', 'dg', 'cg', 'mg']
};
const UNIT_LABELS = {
  // Length
  km: 'kilometer',
  hm: 'hectometer',
  dam: 'decameter',
  m: 'meter',
  dm: 'decimeter',
  cm: 'centimeter',
  mm: 'millimeter',
  // Volume
  kl: 'kiloliter',
  hl: 'hectoliter',
  dal: 'decaliter',
  l: 'liter',
  dl: 'deciliter',
  cl: 'centiliter',
  ml: 'milliliter',
  m3: 'kubieke meter',
  // Weight
  kg: 'kilogram',
  hg: 'hectogram',
  dag: 'decagram',
  g: 'gram',
  dg: 'decigram',
  cg: 'centigram',
  mg: 'milligram'
};
export function MetricStairsCompact({
  type,
  from,
  to,
  value,
  highlight,
  showAnimation = true
}: MetricStairsCompactProps) {
  const units = METRIC_UNITS[type];
  // If from/to specified, show conversion
  if (from && to && value) {
    const fromIndex = units.indexOf(from);
    const toIndex = units.indexOf(to);
    if (fromIndex === -1 || toIndex === -1) {
      return <div className="text-red-500" data-id="element-1312">Ongeldige eenheden</div>;
    }
    // Special case: m3 (cubic meter) conversions
    let steps = Math.abs(toIndex - fromIndex);
    let isGoingDown = toIndex > fromIndex;
    let result: number;
    // Handle m3 ↔ liter conversion specially (1 m³ = 1000 L)
    if (from === 'm3' && to === 'l' || from === 'l' && to === 'm3') {
      if (from === 'm3') {
        // m³ to liters: multiply by 1000
        result = value * 1000;
        isGoingDown = true;
      } else {
        // liters to m³: divide by 1000
        result = value / 1000;
        isGoingDown = false;
      }
      return <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-6 border-2 border-indigo-200" data-id="element-1313">
          <h4 className="font-bold text-indigo-900 mb-4 text-center text-lg" data-id="element-1314">
            {isGoingDown ? '📉 Van kubieke meter naar liter' : '📈 Van liter naar kubieke meter'}
          </h4>

          {/* Simple two-unit display */}
          <div className="flex items-center justify-center gap-4 mb-6" data-id="element-1315">
            <motion.div initial={showAnimation ? {
            scale: 0,
            opacity: 0
          } : {}} animate={{
            scale: 1,
            opacity: 1
          }} className="flex flex-col items-center bg-indigo-600 text-white rounded-lg px-6 py-4 border-2 border-indigo-700 shadow-lg min-w-[100px]" data-id="element-1316">
              <span className="text-3xl font-bold" data-id="element-1317">{from}</span>
              <span className="text-xs mt-1 opacity-75 text-center" data-id="element-1318">
                {UNIT_LABELS[from as keyof typeof UNIT_LABELS]}
              </span>
            </motion.div>

            <motion.div initial={showAnimation ? {
            scale: 0
          } : {}} animate={{
            scale: 1
          }} transition={{
            delay: 0.1
          }} className="flex flex-col items-center" data-id="element-1319">
              <ArrowRight className="w-8 h-8 text-indigo-600" data-id="element-1320" />
              <span className="text-sm font-bold text-indigo-700 mt-1" data-id="element-1321">
                {isGoingDown ? '×1000' : '÷1000'}
              </span>
            </motion.div>

            <motion.div initial={showAnimation ? {
            scale: 0,
            opacity: 0
          } : {}} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.2
          }} className="flex flex-col items-center bg-indigo-600 text-white rounded-lg px-6 py-4 border-2 border-indigo-700 shadow-lg min-w-[100px]" data-id="element-1322">
              <span className="text-3xl font-bold" data-id="element-1323">{to}</span>
              <span className="text-xs mt-1 opacity-75 text-center" data-id="element-1324">
                {UNIT_LABELS[to as keyof typeof UNIT_LABELS]}
              </span>
            </motion.div>
          </div>

          {/* Calculation */}
          <motion.div initial={showAnimation ? {
          opacity: 0,
          y: 10
        } : {}} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="bg-white rounded-lg p-4 border-2 border-indigo-200" data-id="element-1325">
            <div className="text-center" data-id="element-1326">
              <p className="text-slate-700 mb-2" data-id="element-1327">
                <span className="font-bold text-indigo-900" data-id="element-1328">
                  {value} {from}
                </span>
                {' = '}
                <span className="font-bold text-emerald-700" data-id="element-1329">
                  {result.toLocaleString()} {to}
                </span>
              </p>
              <p className="text-sm text-slate-600" data-id="element-1330">
                {isGoingDown ? <>
                    {value} × 1.000 = {result.toLocaleString()}
                  </> : <>
                    {value.toLocaleString()} ÷ 1.000 = {result}
                  </>}
              </p>
            </div>
          </motion.div>

          {/* Tip */}
          <div className="mt-4 bg-amber-50 rounded-lg p-3 border border-amber-200" data-id="element-1331">
            <p className="text-sm text-amber-900 text-center" data-id="element-1332">
              💡 <strong data-id="element-1333">Onthoud:</strong> 1 kubieke meter (m³) = 1.000 liter
            </p>
          </div>
        </div>;
    }
    // Regular conversion (×10 steps)
    const multiplier = isGoingDown ? 10 : 0.1;
    result = isGoingDown ? value * Math.pow(10, steps) : value / Math.pow(10, steps);
    // Show only relevant units (exclude m3 from regular display)
    const startIdx = Math.min(fromIndex, toIndex);
    const endIdx = Math.max(fromIndex, toIndex);
    const relevantUnits = units.slice(startIdx, endIdx + 1).filter(u => u !== 'm3');
    return <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-6 border-2 border-indigo-200" data-id="element-1334">
        <h4 className="font-bold text-indigo-900 mb-4 text-center text-lg" data-id="element-1335">
          {isGoingDown ? '📉 Naar kleinere eenheid' : '📈 Naar grotere eenheid'}
        </h4>

        {/* Horizontal Steps */}
        <div className="flex items-center justify-center gap-2 mb-6 overflow-x-auto pb-2" data-id="element-1336">
          {relevantUnits.map((unit, idx) => <Fragment key={unit} data-id="element-1337">
              <motion.div initial={showAnimation ? {
            scale: 0,
            opacity: 0
          } : {}} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: idx * 0.1
          }} className={`flex flex-col items-center ${unit === from || unit === to ? 'bg-indigo-600 text-white' : 'bg-white text-slate-700'} rounded-lg px-4 py-3 border-2 ${unit === from || unit === to ? 'border-indigo-700 shadow-lg' : 'border-slate-200'} min-w-[70px]`} data-id="element-1338">
                <span className="text-2xl font-bold" data-id="element-1339">{unit}</span>
                <span className="text-xs mt-1 opacity-75 text-center" data-id="element-1340">
                  {UNIT_LABELS[unit as keyof typeof UNIT_LABELS]}
                </span>
              </motion.div>

              {idx < relevantUnits.length - 1 && <motion.div initial={showAnimation ? {
            scale: 0
          } : {}} animate={{
            scale: 1
          }} transition={{
            delay: idx * 0.1 + 0.05
          }} className="flex flex-col items-center" data-id="element-1341">
                  <ArrowRight className="w-6 h-6 text-indigo-600" data-id="element-1342" />
                  <span className="text-xs font-bold text-indigo-700 mt-1" data-id="element-1343">
                    {isGoingDown ? '×10' : '÷10'}
                  </span>
                </motion.div>}
            </Fragment>)}
        </div>

        {/* Calculation */}
        <motion.div initial={showAnimation ? {
        opacity: 0,
        y: 10
      } : {}} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: relevantUnits.length * 0.1
      }} className="bg-white rounded-lg p-4 border-2 border-indigo-200" data-id="element-1344">
          <div className="text-center" data-id="element-1345">
            <p className="text-slate-700 mb-2" data-id="element-1346">
              <span className="font-bold text-indigo-900" data-id="element-1347">
                {value} {from}
              </span>
              {' = '}
              <span className="font-bold text-emerald-700" data-id="element-1348">
                {result} {to}
              </span>
            </p>
            <p className="text-sm text-slate-600" data-id="element-1349">
              {isGoingDown ? <>
                  {value} × {Math.pow(10, steps).toLocaleString()} ={' '}
                  {result.toLocaleString()}
                </> : <>
                  {value} ÷ {Math.pow(10, steps).toLocaleString()} = {result}
                </>}
            </p>
          </div>
        </motion.div>

        {/* Tip */}
        <div className="mt-4 bg-amber-50 rounded-lg p-3 border border-amber-200" data-id="element-1350">
          <p className="text-sm text-amber-900 text-center" data-id="element-1351">
            💡 <strong data-id="element-1352">Onthoud:</strong>{' '}
            {isGoingDown ? 'Kleiner worden = vermenigvuldigen' : 'Groter worden = delen'}
          </p>
        </div>
      </div>;
  }
  // Default: Show all units with optional highlight
  return <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-6 border-2 border-indigo-200" data-id="element-1353">
      <h4 className="font-bold text-indigo-900 mb-4 text-center text-lg" data-id="element-1354">
        {type === 'length' && '📏 Lengtematen'}
        {type === 'volume' && '🧃 Inhoudsmaten'}
        {type === 'weight' && '⚖️ Gewichten'}
      </h4>

      {/* Vertical Layout for Overview */}
      <div className="space-y-2" data-id="element-1355">
        {units.map((unit, idx) => <motion.div key={unit} initial={showAnimation ? {
        x: -20,
        opacity: 0
      } : {}} animate={{
        x: 0,
        opacity: 1
      }} transition={{
        delay: idx * 0.05
      }} className={`flex items-center justify-between p-3 rounded-lg ${unit === highlight ? 'bg-indigo-600 text-white shadow-lg scale-105' : 'bg-white text-slate-700'} border-2 ${unit === highlight ? 'border-indigo-700' : 'border-slate-200'}`} data-id="element-1356">
            <div className="flex items-center gap-3" data-id="element-1357">
              <span className="text-2xl font-bold" data-id="element-1358">{unit}</span>
              <span className="text-sm opacity-75" data-id="element-1359">
                {UNIT_LABELS[unit as keyof typeof UNIT_LABELS]}
              </span>
            </div>

            {idx < units.length - 1 && <div className="flex items-center gap-2 text-xs font-bold opacity-75" data-id="element-1360">
                <ArrowDown className="w-4 h-4" data-id="element-1361" />
                ×10
              </div>}
          </motion.div>)}
      </div>

      {/* Tip */}
      <div className="mt-4 bg-amber-50 rounded-lg p-3 border border-amber-200" data-id="element-1362">
        <p className="text-sm text-amber-900 text-center" data-id="element-1363">
          💡 <strong data-id="element-1364">Onthoud:</strong> Elke stap naar beneden is ×10, elke stap
          naar boven is ÷10
        </p>
      </div>
    </div>;
}